import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

const FlagFR = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 20" width="24" height="16" style={{ borderRadius: 3, display: 'block' }}>
    <rect width="10" height="20" fill="#002395"/>
    <rect x="10" width="10" height="20" fill="#fff"/>
    <rect x="20" width="10" height="20" fill="#ED2939"/>
  </svg>
);

const FlagEN = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="24" height="16" style={{ borderRadius: 3, display: 'block' }}>
    <clipPath id="a"><path d="M0 0v30h60V0z"/></clipPath>
    <clipPath id="b"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath>
    <g clipPath="url(#a)">
      <path d="M0 0v30h60V0z" fill="#012169"/>
      <path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6"/>
      <path d="M0 0l60 30m0-30L0 30" clipPath="url(#b)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

const FlagHE = () => (
  <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 220 160" width="24" height="22" style={{ borderRadius: 3, display: 'block' }}>
    <defs>
      <polygon id="triangle-il" points="0,-29.1410161513775421 -25.2368602791855814,14.5705080756887710 25.2368602791855814,14.5705080756887710" stroke="#0038b8" strokeWidth="5.5" fillOpacity="0"/>
    </defs>
    <rect width="220" height="160" fill="white"/>
    <rect width="220" height="25" fill="#0038b8" y="15"/>
    <rect width="220" height="25" fill="#0038b8" y="120"/>
    <use xlinkHref="#triangle-il" transform="translate(110,80)"/>
    <use xlinkHref="#triangle-il" transform="translate(110,80) rotate(180)"/>
  </svg>
);

const languages = [
  { code: 'fr', Flag: FlagFR, label: 'Français', short: 'FR' },
  { code: 'en', Flag: FlagEN, label: 'English', short: 'EN' },
  { code: 'he', Flag: FlagHE, label: 'עברית', short: 'HE' },
];

const StyleSwitcher = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { darkMode, toggleDarkMode, skinColor, setColorTheme, colorThemes } = useTheme();
  const { t, language, changeLanguage, isRTL } = useLanguage();

  const colorEntries = Object.entries(colorThemes);

  const positionClass = isRTL ? 'left-0' : 'right-0';
  const togglerPosition = isRTL ? 'left-full ml-6' : 'right-full mr-6';
  const transformClass = isOpen
    ? (isRTL ? 'translate-x-6' : '-translate-x-6')
    : (isRTL ? '-translate-x-full' : 'translate-x-full');

  const currentLang = languages.find((l) => l.code === language) || languages[0];

  return (
    <div
      className={`fixed ${positionClass} top-16 p-4 w-[200px] border border-bg-black-50 bg-bg-black-100 z-[101] rounded transform ${transformClass} transition-transform duration-300`}
    >
      {/* ⚙️ Gear toggler */}
      <div
        className={`absolute h-10 w-10 flex items-center justify-center text-xl bg-bg-black-100 text-text-black-900 ${togglerPosition} border border-bg-black-50 cursor-pointer transition-all duration-300 rounded-full`}
        style={{ top: '0px' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="fas fa-cog fa-spin"></i>
      </div>

      {/* 🌙 Day/Night toggler */}
      <div
        className={`absolute h-10 w-10 flex items-center justify-center text-xl bg-bg-black-100 text-text-black-900 ${togglerPosition} border border-bg-black-50 cursor-pointer transition-all duration-300 rounded-full`}
        style={{ top: '56px' }}
        onClick={toggleDarkMode}
      >
        <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'}`}></i>
      </div>

      {/* 🏳️ Language flag circle */}
      <div
        className={`absolute h-10 w-10 flex items-center justify-center bg-bg-black-100 ${togglerPosition} border border-bg-black-50 cursor-pointer transition-all duration-300 rounded-full hover:scale-105`}
        style={{ top: '112px' }}
        onClick={() => setIsOpen(!isOpen)}
        title={currentLang.label}
      >
        <currentLang.Flag />
      </div>

      {/* ── Color theme section ── */}
      <h4 className="m-0 mb-2 text-text-black-700 text-sm font-semibold capitalize">
        {t.styleSwitcher.themeColors}
      </h4>
      <div className="flex flex-wrap justify-between cursor-pointer mb-5">
        {colorEntries.map(([key, value]) => (
          <span
            key={key}
            className={`inline-block h-[30px] w-[30px] rounded-full transition-all duration-200 ${
              skinColor === key ? 'ring-2 ring-offset-1 ring-gray-400 scale-10' : 'hover:scale-105'
            }`}
            style={{ backgroundColor: value }}
            onClick={() => setColorTheme(key)}
          />
        ))}
      </div>

      {/* ── Divider ── */}
      <div className="border-t border-bg-black-50 mb-4" />

      {/* ── Language section ── */}
      <h4 className="m-0 mb-3 text-text-black-700 text-sm font-semibold capitalize">
        {language === 'fr' ? 'Langue' : language === 'en' ? 'Language' : 'שפה'}
      </h4>
      <div className="flex flex-col gap-2">
        {languages.map(({ code, Flag, label, short }) => {
          const isActive = language === code;
          return (
            <button
              key={code}
              onClick={() => changeLanguage(code)}
              title={label}
              aria-label={label}
              className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                isActive
                  ? 'border-skin-color text-text-black-900 bg-bg-black-50 shadow-sm'
                  : 'border-transparent text-text-black-700 hover:bg-bg-black-50 opacity-70 hover:opacity-100'
              }`}
            >
              <span className="flex-shrink-0 rounded overflow-hidden shadow-sm">
                <Flag />
              </span>
              <span className="flex-1 text-left">{label}</span>
              {isActive && (
                <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--skin-color)' }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StyleSwitcher;